import { dollars } from './buildings';
import type { Rng } from './rng';

/*
 * Daily goals: every morning three small goals for the day, sized on what the airport did the
 * day before (a little more of it), each paying a reward when met. Some are met as soon as the
 * count is reached (departures, passengers, revenue, freight); the others (on time, nothing
 * cancelled) are judged when the day ends, over a day with enough traffic to count.
 */

/** What a goal asks for. Saved: append only. */
export const DailyGoalKind = {
  Departures: 0,
  Passengers: 1,
  Revenue: 2,
  Tonnes: 3,
  OnTime: 4,
  NoCancellations: 5,
} as const;
export type DailyGoalKindId = (typeof DailyGoalKind)[keyof typeof DailyGoalKind];

export interface DailyGoal {
  readonly kind: DailyGoalKindId;
  /** The count to reach (on time: percent of departures). */
  readonly target: number;
  /** Cents paid when met. */
  readonly reward: number;
  /** 1 once met (and paid), 2 once missed (judged at the end of the day). */
  state: number;
}

export const GoalState = { Open: 0, Met: 1, Missed: 2 } as const;

/** Goals a day. */
export const DAILY_GOALS = 3;
/** Goals grow on yesterday's figures by this much (percent). */
export const GOAL_STRETCH_PERCENT = 115;
/** On-time goal: this share of departures on time, out of at least this many. */
export const ON_TIME_GOAL_PERCENT = 85;
export const JUDGED_MIN_DEPARTURES = 6;
/** A reward is a share of yesterday's income (percent), within these bounds. */
export const GOAL_REWARD_PERCENT = 6;
export const GOAL_REWARD_MIN = dollars(4_000);
export const GOAL_REWARD_MAX = dollars(250_000);

/** What yesterday was like, for sizing today's goals. */
export interface GoalBasis {
  departures: number;
  passengers: number;
  income: number;
  tonnes: number;
}

/** What today has brought so far. */
export interface GoalProgress extends GoalBasis {
  onTime: number;
  cancelled: number;
}

const grow = (n: number, min: number) => Math.max(min, Math.ceil((n * GOAL_STRETCH_PERCENT) / 100));

/** Today's goals: three different ones that suit the airport (freight only with freight). */
export function rollGoals(rng: Rng, yesterday: GoalBasis): DailyGoal[] {
  const reward = Math.max(
    GOAL_REWARD_MIN,
    Math.min(GOAL_REWARD_MAX, Math.floor((yesterday.income * GOAL_REWARD_PERCENT) / 100)),
  );
  const candidates: { kind: DailyGoalKindId; target: number; weight: number }[] = [
    { kind: DailyGoalKind.Departures, target: grow(yesterday.departures, 4), weight: 3 },
    { kind: DailyGoalKind.Passengers, target: grow(yesterday.passengers, 150), weight: 3 },
    { kind: DailyGoalKind.Revenue, target: grow(yesterday.income, dollars(20_000)), weight: 2 },
  ];
  if (yesterday.tonnes > 0) {
    candidates.push({ kind: DailyGoalKind.Tonnes, target: grow(yesterday.tonnes, 10), weight: 2 });
  }
  if (yesterday.departures >= JUDGED_MIN_DEPARTURES) {
    candidates.push({ kind: DailyGoalKind.OnTime, target: ON_TIME_GOAL_PERCENT, weight: 2 });
    candidates.push({ kind: DailyGoalKind.NoCancellations, target: 0, weight: 1 });
  }
  const goals: DailyGoal[] = [];
  while (goals.length < DAILY_GOALS && candidates.length > 0) {
    const total = candidates.reduce((n, c) => n + c.weight, 0);
    let pick = rng.int(total);
    let i = 0;
    while (pick >= (candidates[i]?.weight ?? 0)) {
      pick -= candidates[i]?.weight ?? 0;
      i++;
    }
    const c = candidates.splice(i, 1)[0];
    if (!c) break;
    // The day-long ones are worth a little more.
    const judged = c.kind === DailyGoalKind.OnTime || c.kind === DailyGoalKind.NoCancellations;
    goals.push({
      kind: c.kind,
      target: c.target,
      reward: judged ? Math.floor((reward * 3) / 2) : reward,
      state: GoalState.Open,
    });
  }
  return goals;
}

/** How far today has got towards a goal (its own unit; on time: percent so far). */
export function goalValue(goal: DailyGoal, today: GoalProgress): number {
  switch (goal.kind) {
    case DailyGoalKind.Departures:
      return today.departures;
    case DailyGoalKind.Passengers:
      return today.passengers;
    case DailyGoalKind.Revenue:
      return today.income;
    case DailyGoalKind.Tonnes:
      return today.tonnes;
    case DailyGoalKind.OnTime:
      return today.departures > 0 ? Math.floor((today.onTime * 100) / today.departures) : 0;
    default:
      return today.cancelled;
  }
}

/** Judged only when the day is over (on time, nothing cancelled). */
export function judgedAtDayEnd(goal: DailyGoal): boolean {
  return goal.kind === DailyGoalKind.OnTime || goal.kind === DailyGoalKind.NoCancellations;
}

/** Whether a goal is met by today's figures (for a judged one: if the day ended now). */
export function goalMet(goal: DailyGoal, today: GoalProgress): boolean {
  const value = goalValue(goal, today);
  if (goal.kind === DailyGoalKind.NoCancellations) {
    return value === 0 && today.departures >= JUDGED_MIN_DEPARTURES;
  }
  if (goal.kind === DailyGoalKind.OnTime) {
    return today.departures >= JUDGED_MIN_DEPARTURES && value >= goal.target;
  }
  return value >= goal.target;
}
